<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CustomToolForm from './CustomToolForm.vue';
import AgentConfirmDialog from 'dashboard/routes/dashboard/captain/assistants/experience/components/AgentConfirmDialog.vue';

const props = defineProps({
  selectedTool: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const pendingDisabledTool = ref(null);

const scenarios = computed(
  () => store.getters['captainScenarios/getRecords'] || []
);
const linkedScenarioCount = computed(() => {
  const slug = String(props.selectedTool?.slug || '');
  if (!slug) return 0;
  return scenarios.value.filter(scenario =>
    (scenario.tools || []).map(String).includes(slug)
  ).length;
});

const updateTool = toolDetails =>
  store.dispatch('captainCustomTools/update', {
    id: props.selectedTool.id,
    ...toolDetails,
  });

const dialogTitle = computed(() =>
  props.type === 'edit'
    ? t('CAPTAIN.CUSTOM_TOOLS.EDIT.TITLE')
    : t('CAPTAIN.CUSTOM_TOOLS.CREATE.TITLE')
);

const successMessage = computed(() =>
  props.type === 'edit'
    ? t('CAPTAIN.CUSTOM_TOOLS.EDIT.SUCCESS_MESSAGE')
    : t('CAPTAIN.CUSTOM_TOOLS.CREATE.SUCCESS_MESSAGE')
);

const fallbackErrorMessage = computed(() =>
  props.type === 'edit'
    ? t('CAPTAIN.CUSTOM_TOOLS.EDIT.ERROR_MESSAGE')
    : t('CAPTAIN.CUSTOM_TOOLS.CREATE.ERROR_MESSAGE')
);

const createTool = toolDetails =>
  store.dispatch('captainCustomTools/create', toolDetails);

const persistTool = async updatedTool => {
  try {
    if (props.type === 'edit') {
      await updateTool(updatedTool);
    } else {
      await createTool(updatedTool);
    }
    useAlert(successMessage.value);
    dialogRef.value.close();
  } catch (error) {
    const alertMessage =
      parseAPIErrorResponse(error) || fallbackErrorMessage.value;
    useAlert(alertMessage);
  }
};

const handleSubmit = updatedTool => {
  if (
    props.type === 'edit' &&
    props.selectedTool?.enabled !== false &&
    updatedTool.enabled === false &&
    linkedScenarioCount.value > 0
  ) {
    pendingDisabledTool.value = { ...updatedTool };
    return;
  }

  persistTool(updatedTool);
};

const confirmDisableTool = () => {
  const updatedTool = pendingDisabledTool.value;
  pendingDisabledTool.value = null;
  if (updatedTool) persistTool(updatedTool);
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="dialogTitle"
    :description="$t('CAPTAIN.CUSTOM_TOOLS.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <CustomToolForm
      :mode="type"
      :tool="selectedTool"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
  <AgentConfirmDialog
    :show="Boolean(pendingDisabledTool)"
    :title="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_CUSTOM_TITLE')"
    :description="
      $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_CUSTOM_DESCRIPTION', {
        count: linkedScenarioCount,
      })
    "
    :confirm-text="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_CUSTOM')"
    @close="pendingDisabledTool = null"
    @confirm="confirmDisableTool"
  />
</template>
